import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Instance axios configurée
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Intercepteur pour ajouter le token JWT
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Endpoints publics d'authentification: un 401 ici est une erreur normale
// (mauvais mot de passe, etc.) à afficher inline — PAS une session expirée.
const PUBLIC_AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-admin-login',
  '/auth/resend-admin-code'
];

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isPublicAuthCall = PUBLIC_AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

    // Session expirée / token invalide sur un appel authentifié uniquement.
    // Sur un endpoint public (ex: mauvais mot de passe au login), on laisse
    // le composant appelant gérer l'erreur — sinon la page se recharge
    // entièrement et efface le message d'erreur avant qu'il ne s'affiche.
    if (error.response?.status === 401 && !isPublicAuthCall) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
  getAppInfo: () => api.get('/auth/app-info'),
  confirmCapital: () => api.post('/auth/confirm-capital'),
  investReferralBonus: () => api.post('/auth/invest-referral-bonus'),
  submitKYC: (documentType, documentNumber) =>
    api.post('/auth/kyc/submit', { documentType, documentNumber }),
  getKYCStatus: () => api.get('/auth/kyc/status'),
  submitWithdrawalAddress: (address) => api.post('/auth/withdrawal-address', { address }),
  getWithdrawalAddress: () => api.get('/auth/withdrawal-address'),
  verifyEmail: (email, code) => api.post('/auth/verify-email', { email, code }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (email, code, newPassword) => api.post('/auth/reset-password', { email, code, newPassword }),
  verifyAdminLogin: (email, code) => api.post('/auth/verify-admin-login', { email, code }),
  resendAdminCode: (email) => api.post('/auth/resend-admin-code', { email })
};

// Crypto API
export const cryptoAPI = {
  getPrices: (currency = 'usd') => api.get(`/crypto/prices?currency=${currency}`),
  getCrypto: (symbol, currency = 'usd') => api.get(`/crypto/${symbol}?currency=${currency}`),
  getChart: (symbol, period = '24h') => api.get(`/crypto/${symbol}/chart?period=${period}`),
  getCurrencies: () => api.get('/crypto/currencies')
};

// Portfolio API
export const portfolioAPI = {
  get: () => api.get('/portfolio'),
  getSummary: () => api.get('/portfolio/summary')
};

// Recommendations API
export const recommendationsAPI = {
  getAll: (status) => api.get('/recommendations', { params: { status } }),
  getPending: () => api.get('/recommendations/pending'),
  getActive: () => api.get('/recommendations/active'),
  getCompleted: () => api.get('/recommendations/completed'),
  getExpired: () => api.get('/recommendations/expired'),
  accept: (id) => api.post(`/recommendations/${id}/accept`),
  reject: (id) => api.post(`/recommendations/${id}/reject`)
};

// Transactions API
export const transactionsAPI = {
  getAll: (params) => api.get('/transactions', { params }),
  getSummary: () => api.get('/transactions/summary')
};

// Stats API
export const statsAPI = {
  get: () => api.get('/stats'),
  getBot: () => api.get('/stats/bot'),
  getHistory: (period) => api.get('/stats/history', { params: { period } })
};

// Withdrawals API
export const withdrawalsAPI = {
  getStatus: () => api.get('/withdrawals/status'),
  getHistory: () => api.get('/withdrawals/history'),
  request: (amount) => api.post('/withdrawals/request', { amount }),
  skip: (months) => api.post('/withdrawals/skip', { months }),
  cancel: (id) => api.delete(`/withdrawals/${id}`)
};

// Deposits API
export const depositsAPI = {
  generate: () => api.post('/deposits/generate'),
  getAddress: () => api.get('/deposits/address'),
  submitTxid: (txid, amount) => api.post('/deposits/submit-txid', { txid, amount }),
  getHistory: () => api.get('/deposits/history')
};

// Portefeuilles épargne (utilisateur)
export const savingsAPI = {
  getMine: () => api.get('/savings/mine'),
  withdraw: (walletId, amount) => api.post(`/savings/${walletId}/withdraw`, { amount })
};

// Config API
export const configAPI = {
  getTradingSummary: () => api.get('/config/summary/trading')
};

// Messages API
export const messagesAPI = {
  getAll: () => api.get('/messages'),
  send: (content) => api.post('/messages', { content }),
  getUnreadCount: () => api.get('/messages/unread-count')
};

export default api;
